import { boardblendApi } from "@/store/api";
import type { JoinResult, SavedSession } from "@/types/session";

interface CreateSessionArgs {
  displayName?: string;
  title?: string;
}

interface JoinSessionArgs {
  displayName: string;
  roomCode?: string;
}

interface ForkShowcaseArgs {
  displayName?: string;
  slug: string;
}

interface RemoveAccountSessionArgs {
  action: "delete" | "remove";
  confirmation?: string;
  sessionId: string;
}

export const entryApi = boardblendApi.injectEndpoints({
  endpoints: (build) => ({
    createSession: build.mutation<JoinResult, CreateSessionArgs | void>({
      query: (body) => ({ body: body ?? {}, method: "POST", url: "sessions" }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ id: "LIST", type: "AccountSessions" }],
    }),
    joinSession: build.mutation<JoinResult, JoinSessionArgs>({
      query: (body) => ({ body, method: "POST", url: "join" }),
    }),
    forkShowcase: build.mutation<{ sessionId: string }, ForkShowcaseArgs>({
      query: ({ slug, ...body }) => ({
        body,
        method: "POST",
        url: `showcases/${slug}/fork`,
      }),
    }),
    saveSession: build.mutation<unknown, string>({
      query: (sessionId) => ({
        method: "POST",
        url: `sessions/${sessionId}/save`,
      }),
      invalidatesTags: (_result, error, sessionId) =>
        error
          ? []
          : [
              { id: "LIST", type: "AccountSessions" },
              { id: sessionId, type: "Session" },
            ],
    }),
    getAccountSessions: build.query<SavedSession[], void>({
      query: () => "account/sessions",
      transformResponse: (response: { sessions: SavedSession[] }) =>
        response.sessions,
      providesTags: [{ id: "LIST", type: "AccountSessions" }],
    }),
    removeAccountSession: build.mutation<unknown, RemoveAccountSessionArgs>({
      query: ({ sessionId, ...body }) => ({
        body,
        method: "DELETE",
        url: `account/sessions/${sessionId}`,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ id: "LIST", type: "AccountSessions" }],
      onQueryStarted: async ({ sessionId }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          entryApi.util.updateQueryData(
            "getAccountSessions",
            undefined,
            (sessions) =>
              sessions.filter((session) => session.id !== sessionId),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useForkShowcaseMutation,
  useGetAccountSessionsQuery,
  useJoinSessionMutation,
  useRemoveAccountSessionMutation,
  useSaveSessionMutation,
} = entryApi;

export type SaveSessionTrigger = ReturnType<typeof useSaveSessionMutation>[0];
